import React, { useEffect, useState } from 'react';
import { TrackSelectPage as PageType, TrackCatalogEntry, UserProfile } from '../../types/quiz';
import { useLang } from '../../i18n/LangContext';
import '../../assets/scss/quiz-editorial.scss';

interface Props {
    page: PageType;
    userProfile: UserProfile;
    tracks?: Record<string, TrackCatalogEntry>;
    // Stores the chosen track code (drives which track quiz loads next).
    onAnswer: (value: string) => void;
}

/**
 * "Where do we start?" — after routing we surface an insight about the weakest
 * (recommended) direction, then offer all directions as tags. The recommended
 * one is pre-selected; the user can start with any of them.
 */
const TrackSelectPage: React.FC<Props> = ({ page, userProfile, tracks, onAnswer }) => {
    const { lang } = useLang();
    const uk = lang === 'uk';

    const recommended = (userProfile.recommended_track as string) || '';
    const all: TrackCatalogEntry[] = tracks ? Object.values(tracks) : [];
    const rec = tracks && recommended ? tracks[recommended] : undefined;

    const savedTrack = userProfile[page.data_field || 'selected_track'] as string | undefined;
    const [selected, setSelected] = useState<string>(savedTrack || recommended || (all[0] && all[0].code) || '');

    useEffect(() => {
        setSelected(savedTrack || recommended || (all[0] && all[0].code) || '');
    }, [page.id, recommended]); // eslint-disable-line

    // The heading keeps the fixed recommendation; the insight block previews
    // whichever direction is currently selected, so it switches as the user taps
    // tags (key={selected} replays the fade on each switch).
    const sel = tracks && selected ? tracks[selected] : undefined;
    const recLabel = rec ? rec.label : '';
    const recTitle = uk
        ? `Рекомендуємо почати з напрямку ${recLabel.toUpperCase()}`
        : `We recommend starting with ${recLabel}`;
    const title = rec ? recTitle : page.title;

    const commit = () => {
        if (selected) onAnswer(selected);
    };

    return (
        <div className="page page--track-select">
            <div className="page__container">
                <h2 className="page__question">{title}</h2>

                {sel && (
                    <div className="track-insight" key={selected}>
                        <h3 className="track-insight__title">{sel.label}</h3>
                        {sel.opis && <p className="track-insight__body">{sel.opis}</p>}

                        {/*
                          * Collapsed by default: the direction picker and the
                          * button below are what this screen is FOR, and three
                          * benefit lines pushed them off a phone. Anyone who
                          * wants the detail is one tap away.
                          */}
                        {sel.benefits && sel.benefits.length > 0 && (
                            <details className="track-insight__benefits">
                                <summary className="track-insight__benefits-label">
                                    {uk ? 'Що зміниться' : 'What changes'}
                                </summary>
                                <ul>
                                    {sel.benefits.map((b) => (
                                        <li key={b}>{b}</li>
                                    ))}
                                </ul>
                            </details>
                        )}

                        {sel.insight && (
                            <p className="track-insight__aha">
                                <span aria-hidden>💡</span> {sel.insight}
                            </p>
                        )}
                    </div>
                )}

                <p className="track-select__prompt">
                    {uk ? 'Почати можеш із будь-якого напряму:' : 'You can start with any direction:'}
                </p>

                <div className="track-tags">
                    {all.map((tr) => {
                        const isSel = selected === tr.code;
                        const isRec = tr.code === recommended;

                        return (
                            <button
                                key={tr.code}
                                type="button"
                                className={`track-tag ${isSel ? 'track-tag--selected' : ''} ${
                                    isRec ? 'track-tag--recommended' : ''
                                }`}
                                onClick={() => setSelected(tr.code)}
                                aria-pressed={isSel}
                            >
                                {tr.label}
                                {isRec && (
                                    <span className="track-tag__star" aria-hidden>
                                        ★
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="page__actions">
                    <button type="button" className="page__button page__button--block" onClick={commit} disabled={!selected}>
                        {page.buttonText || (uk ? 'Продовжити →' : 'Continue →')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TrackSelectPage;
